import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Stack,
  Box,
  IconButton,
  Button,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  Add,
  InfoOutlined,
  EventNote,
  Schedule,
  Security,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import CustomInput from "../../components/FormFields/CustomInput";
import CustomCheckbox from "../../components/CustomForm/CustomCheckbox";
import MultiSelect from "../../components/FormFields/CustomSelect/MultiSelect";
import CustomTextArea from "../../components/CustomForm/CustomTextArea";
import CustomDateTime from "../../components/CustomForm/CustomDateTime";
import CustomTimePicker from "../../components/FormFields/TiimePicker/TimePicker";
import CarePlanPickerDialog from "../../components/Clients/CarePlan/Dialog";
import FundingModal from "../../components/Clients/Funding/FundingModal.js";
import Loader from "../../components/Loader/Loader";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";
import DaySelector from "../../screens/VisitPlanScreen/VisitPlanModal/EditSchedule/DaySelector";
import TimeSlots from "../../screens/VisitPlanScreen/VisitPlanModal/EditSchedule/TimeSlots";

import { createVisit } from "../../api/services/scheduling";
import { getCheckList, getClientPaymentServices } from "../../api/services/core";
import { getActiveCarePlans } from "../../api/services/carePathWay";
import { useUserProfile } from "../../hooks/userProfile";
import { QK } from "../../base/qk";
import { VisitTypes } from "../../components/Clients/ClientDetails/WhatElse/VisitAnalysis/visit.config";

const CreateClientVisit = ({ clientID, setActiveKey, setVisitId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useUserProfile();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    visit_name: "",
    care_plan: "",
    payment_service: "",
    carers_required: 1,
    repeat_every: 2,
    time_tolerance: 60,
    personalized_checks: [{ id: Date.now(), check_name: "" }],
    time_required_from: "",
    time_required_to: "",
    start_time: "",
    end_time: "",
    support_instructions: "",
    allow_photos: false,
    share_with_family: false,
    mandatory_notes: false,
    time_critical: false,
  });

  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customTimeData, setCustomTimeData] = useState({});
  const [totalWeeklyHours, setTotalWeeklyHours] = useState("0h 0m");
  const [isTimeCritical, setIsTimeCritical] = useState(false);
  const [isContinueIndifinite, setIsContinueIndifinite] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showCarePlanDialog, setShowCarePlanDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visitType, setVisitType] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedCheckList, setselectedCheckList] = useState([]);

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const { data: activeCarePlans = [], isLoading: isLoadingCarePlans, refetch: refetchActiveCarePlans } = useQuery(
    [QK.activeCarePlans, { client: clientID }],
    () => getActiveCarePlans({ client: clientID }),
    { enabled: !!clientID }
  );
  const { data: checkList } = useQuery([QK.checklist], () => getCheckList());
  const { data: paymentServices } = useQuery(
    [QK.fundingProfiles, clientID],
    () => getClientPaymentServices(clientID),
    { enabled: !!clientID }
  );

  useEffect(() => {
    if (activeCarePlans && formData.care_plan) {
      const exists = activeCarePlans.some(p => String(p.id) === String(formData.care_plan));
      if (!exists) {
        const matched = activeCarePlans.find(p => String(p.form) === String(formData.care_plan));
        if (matched) {
          setFormData(prev => ({ ...prev, care_plan: matched.id }));
        } else {
          setFormData(prev => ({ ...prev, care_plan: "" }));
        }
      }
    }
  }, [activeCarePlans]);

  const carePlanOptions = activeCarePlans?.map((p) => ({
    value: p.id,
    label: p.form_title || p.title || p.name || `Care Plan ${p.id}`
  })) || [];

  const paymentServiceOptions = paymentServices?.map((p) => ({
    value: p.id || p.value,
    label: p.service || p.name || p.label || `Service ${p.id}`
  })) || [];

  const checkListOptions = checkList?.map((c) => ({
    value: c.id || c.value,
    label: c.name || c.label || `Check ${c.id}`
  })) || [];

  const visitTypeOptions = VisitTypes?.map((v) => ({
    value: v.id || v.value,
    label: v.label || v.name || `Type ${v.id}`
  })) || [];

  const formatTotalMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const calculateTotalWeeklyHours = (timeSource) => {
    let totalMinutes = 0;

    if (isCustomTime) {
      Object.values(timeSource).forEach((slots) => {
        if (Array.isArray(slots)) {
          slots.forEach((slot) => {
            if (slot.time_required_from && slot.time_required_to) {
              totalMinutes += getTimeDiff(slot.time_required_from, slot.time_required_to);
            }
          });
        }
      });
    } else {
      if (formData.time_required_from && formData.time_required_to) {
        const dailyDiff = getTimeDiff(formData.time_required_from, formData.time_required_to);
        totalMinutes = dailyDiff * selectedDays.length;
      }
    }

    setTotalWeeklyHours(formatTotalMinutes(totalMinutes));
    setFormData(prev => ({ ...prev, total_duration: totalMinutes }));
  };

  const getTimeDiff = (start, end) => {
    const [fH, fM] = start.split(":").map(Number);
    const [tH, tM] = end.split(":").map(Number);
    let fromMin = fH * 60 + fM;
    let toMin = tH * 60 + tM;
    if (toMin < fromMin) toMin += 1440;
    return toMin - fromMin;
  };

  const handleChange = (e) => {
    const { name, checked, type, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    const finalValue = type === "number" ? Number(value) : newValue;
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (name === "time_required_from" || name === "time_required_to") {
      if (!isCustomTime) calculateTotalWeeklyHours(customTimeData);
    }
  };

  const handleCheckBox = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (name === "time_critical") setIsTimeCritical(checked);
    if (name === "continue_indefinitely") {
      setIsContinueIndifinite(checked);
      setFormData(prev => ({ ...prev, end_time: "" }));
    }
  };

  const handleVisitTypeChange = (e) => {
    const val = Number(e.target.value);
    setVisitType(val);
    setFormData(prev => ({ ...prev, repeat_every: val }));
    if (val === 1) {
      setSelectedDays(daysOfWeek);
    } else {
      setSelectedDays([]);
      setCustomTimeData({});
    }
  };

  const handleDaySelection = (day) => {
    setSelectedDays((prev) => {
      const isSelected = prev.includes(day);
      const updatedDays = isSelected ? prev.filter((d) => d !== day) : [...prev, day];

      const newTimeData = { ...customTimeData };
      if (isSelected) {
        delete newTimeData[day];
      } else {
        newTimeData[day] = [{ time_required_from: "", time_required_to: "" }];
      }

      setCustomTimeData(newTimeData);
      return updatedDays;
    });
  };

  useEffect(() => {
    calculateTotalWeeklyHours(customTimeData);
  }, [selectedDays, isCustomTime]);

  const handleCustomTimeChange = (day, index, key, value) => {
    const slots = Array.isArray(customTimeData[day]) ? [...customTimeData[day]] : [];
    slots[index] = { ...slots[index], [key]: value };
    
    const newData = {
      ...customTimeData,
      [day]: slots,
    };
    
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleAddSlot = (day) => {
    const newSlot = {
      id: null,
      time_required_from: "",
      time_required_to: "",
    };
    
    const currentSlots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
    const newData = {
      ...customTimeData,
      [day]: [...currentSlots, newSlot],
    };
    
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleRemoveSlot = (day, index) => {
    const slots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
    const newSlots = slots.filter((_, i) => i !== index);
    const newData = {
      ...customTimeData,
      [day]: newSlots,
    };
    
    if (newSlots.length === 0) {
      delete newData[day];
      setSelectedDays(selectedDays.filter((d) => d !== day));
    }
    
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleMultiSelectChange = (options) => {
    setselectedCheckList(options);
  };

  const handleAddInput = () => {
    setFormData(prev => ({
      ...prev,
      personalized_checks: [...(prev.personalized_checks || []), { id: Date.now(), check_name: "" }]
    }));
  };

  const handleRemoveInput = (id) => {
    setFormData(prev => ({ ...prev, personalized_checks: prev.personalized_checks.filter(c => c.id !== id) }));
  };

  const handlePersonalizedCheck = (index, e) => {
    const value = e.target.value;
    setFormData(prev => {
      const newChecks = [...prev.personalized_checks];
      newChecks[index].check_name = value;
      return { ...prev, personalized_checks: newChecks };
    });
  };

  const handleCarePlanSelect = (formId, label) => {
    const matchedPlan = activeCarePlans?.find(p => p.form === formId);
    if (matchedPlan) {
      setFormData(prev => ({
        ...prev,
        care_plan: matchedPlan.id
      }));
    } else {
      refetchActiveCarePlans();
      setFormData(prev => ({
        ...prev,
        care_plan: formId
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    let weeklyTimeList = [];

    if (isCustomTime) {
      selectedDays.forEach((day) => {
        const slots = customTimeData[day] || [];
        slots.forEach((slot) => {
          if (slot.time_required_from && slot.time_required_to) {
            weeklyTimeList.push({
              [day]: {
                time_required_from: slot.time_required_from,
                time_required_to: slot.time_required_to
              }
            });
          }
        });
      });
    } else {
      weeklyTimeList = selectedDays.map((day) => ({
        [day]: {
          time_required_from: formData.time_required_from || "00:00",
          time_required_to: formData.time_required_to || "00:00"
        }
      }));
    }

    const requestData = {
      ...formData,
      time_required_from: formData.time_required_from || "00:00",
      time_required_to: formData.time_required_to || "00:00",
      visit_type: 3,
      recurrence_type: visitType === 2 ? 2 : 1,
      weekly_time_data: weeklyTimeList,
      user: user?.id,
      client: clientID,
      checklist: selectedCheckList?.map((item) => item.value),
      start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
      end_time: formData.end_time && !isContinueIndifinite ? new Date(formData.end_time).toISOString() : null,
    };

    try {
      const response = await createVisit(requestData);
      if (response.status === 201) {
        enqueueSnackbar("Visit created successfully.", { variant: "success" });
        queryClient.invalidateQueries(QK.visits);
        setVisitId(response.data.id);
        setActiveKey("assign-employee");
      }
    } catch (error) {
      enqueueSnackbar("Error creating Visit. Please check your inputs.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingCarePlans) return <Loader />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: "1 1 48%", minWidth: "320px" }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "#F8FAFC",
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EventNote sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                  Visit Details
                </Typography>
                <Chip
                  label="New"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    bgcolor: "#D1FAE5",
                    color: "#039855",
                  }}
                />
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                  <CustomInput
                    label="Visit Name *"
                    name="visit_name"
                    value={formData.visit_name || ""}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="medium"
                  />

                  <Box>
                    <CustomSelect
                      options={carePlanOptions}
                      label="Care Plan *"
                      name="care_plan"
                      value={formData.care_plan}
                      onChange={handleChange}
                      required
                      fullWidth
                      placeholder="Select Care Plan"
                    />
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                      onClick={() => setShowCarePlanDialog(true)}
                      sx={{
                        mt: 0.5,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        color: theme.palette.primary.main,
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      Browse Care Plan
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 2 }}>
                      <CustomSelect
                        options={paymentServiceOptions}
                        label="Service *"
                        name="payment_service"
                        value={formData.payment_service}
                        onChange={handleChange}
                        required
                        fullWidth
                        placeholder="Select Funding Profile"
                      />
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                        onClick={() => setShowFundingModal(true)}
                        sx={{
                          mt: 0.5,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          color: theme.palette.primary.main,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        }}
                      >
                        Add Funding Profile
                      </Button>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <CustomInput
                        label="Carers *"
                        name="carers_required"
                        type="number"
                        value={formData.carers_required || 1}
                        onChange={handleChange}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" fontWeight={600} color="#475467" sx={{ mb: 0.5, display: "block" }}>
                      CHECKLISTS
                    </Typography>
                    <MultiSelect
                      options={checkListOptions}
                      value={selectedCheckList}
                      onChange={handleMultiSelectChange}
                      placeholder="Select checklists..."
                    />
                  </Box>

                  <Box>
                    <Typography variant="caption" fontWeight={600} color="#475467" sx={{ mb: 0.5, display: "block" }}>
                      PERSONALIZED CHECKS
                    </Typography>
                    <Stack spacing={0.5}>
                      {formData.personalized_checks?.map((input, index) => (
                        <Stack key={input.id} direction="row" spacing={0.5} alignItems="center">
                          <Box flexGrow={1}>
                            <CustomInput
                              placeholder="New check..."
                              value={input.check_name}
                              onChange={(e) => handlePersonalizedCheck(index, e)}
                              size="small"
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveInput(input.id)}
                            sx={{ color: "#EF4444", p: 0.5, "&:hover": { bgcolor: alpha("#EF4444", 0.04) } }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                    <Button
                      size="small"
                      startIcon={<Add sx={{ fontSize: 16 }} />}
                      onClick={handleAddInput}
                      sx={{
                        mt: 0.5,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        color: theme.palette.primary.main,
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      Add Check
                    </Button>
                  </Box>

                  <CustomTextArea
                    label="Type Instructions Here"
                    name="support_instructions"
                    value={formData.support_instructions || ""}
                    onChange={handleChange}
                    rows={3}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 1.5,
                      bgcolor: "#F8FAFC",
                      borderRadius: "6px",
                      border: "1px dashed #D0D5DD",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <CustomDateTime
                        name="start_time"
                        label="Start Date *"
                        onChange={handleChange}
                        value={formData.start_time || ""}
                        required
                        format="YYYY-MM-DD HH:mm"
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CustomCheckbox
                        label="Indefinite"
                        name="continue_indefinitely"
                        checked={formData.continue_indefinitely || false}
                        onClick={(c) => handleCheckBox("continue_indefinitely", c)}
                      />
                    </Box>
                  </Box>

                  {!isContinueIndifinite && (
                    <CustomDateTime
                      name="end_time"
                      label="End Date"
                      onChange={handleChange}
                      value={formData.end_time || ""}
                      format="YYYY-MM-DD HH:mm"
                    />
                  )}
                </Stack>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: "1 1 48%", minWidth: "320px" }}>
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #E2E8F0",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: alpha("#F59E0B", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Schedule sx={{ color: "#F59E0B", fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                      Scheduling
                    </Typography>
                    <Chip
                      label={`${selectedDays.length} days`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Stack>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" fontSize="0.7rem">
                      {totalWeeklyHours}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={2}>
                    <CustomSelect
                      label="Frequency"
                      name="repeat_every"
                      options={visitTypeOptions}
                      value={formData.repeat_every}
                      onChange={handleVisitTypeChange}
                      fullWidth
                      placeholder="Select Frequency"
                    />

                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: "6px",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} color="#475467" sx={{ mb: 1, display: "block", fontSize: "0.65rem", letterSpacing: "0.3px", textTransform: "uppercase" }}>
                        SELECT DAYS
                      </Typography>

                      <DaySelector
                        daysOfWeek={daysOfWeek}
                        selectedDays={selectedDays}
                        weeklyAssignment={[]}
                        onDaySelect={handleDaySelection}
                      />

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, mb: 1.5 }}>
                        <CustomCheckbox
                          label="Custom times per day"
                          checked={isCustomTime}
                          onClick={() => setIsCustomTime(!isCustomTime)}
                        />
                        <Tooltip title="Set unique times for each day">
                          <InfoOutlined sx={{ fontSize: 14, color: "#94A3B8" }} />
                        </Tooltip>
                      </Box>

                      {isCustomTime && selectedDays.length > 0 && (
                        <TimeSlots
                          selectedDays={selectedDays}
                          customTimeData={customTimeData}
                          onTimeChange={handleCustomTimeChange}
                          onAddSlot={handleAddSlot}
                          onRemoveSlot={handleRemoveSlot}
                          weeklyAssignment={[]}
                        />
                      )}

                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: "1px solid #E2E8F0",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} color="#475467">
                          Total Weekly Hours
                        </Typography>
                        <Chip
                          label={totalWeeklyHours}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            bgcolor: alpha("#EF4444", 0.08),
                            color: "#EF4444",
                          }}
                        />
                      </Box>
                    </Box>

                    {!isCustomTime && (
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <CustomTimePicker
                          label="Global From"
                          name="time_required_from"
                          value={formData.time_required_from || ""}
                          onChange={handleChange}
                          size="small"
                        />
                        <CustomTimePicker
                          label="Global To"
                          name="time_required_to"
                          value={formData.time_required_to || ""}
                          onChange={handleChange}
                          size="small"
                        />
                      </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <CustomCheckbox
                        label="Time Critical?"
                        name="time_critical"
                        checked={formData.time_critical || false}
                        onClick={(c) => handleCheckBox("time_critical", c)}
                      />
                      {!isTimeCritical && (
                        <CustomInput
                          label="Tol. (min)"
                          name="time_tolerance"
                          type="number"
                          value={formData.time_tolerance || 60}
                          onChange={handleChange}
                          size="small"
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #E2E8F0",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "#F0F7FF",
                    borderBottom: "1px solid #E2E8F0",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: alpha("#2196F3", 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Security sx={{ color: "#2196F3", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="#1A3E72">
                      Safety & Compliance
                    </Typography>
                    <Typography variant="caption" color="#5F7D95" sx={{ fontSize: "0.6rem" }}>
                      Manage security protocols
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    {[
                      { label: "Allow Photos", name: "allow_photos", desc: "Enable image evidence" },
                      { label: "Share with Family", name: "share_with_family", desc: "Update family portal" },
                      { label: "Mandatory Notes", name: "mandatory_notes", desc: "Required visit notes" },
                    ].map((item) => (
                      <Box
                        key={item.name}
                        sx={{
                          p: 1.5,
                          borderRadius: "6px",
                          border: "1px solid #E2E8F0",
                          bgcolor: formData[item.name] ? alpha(theme.palette.primary.main, 0.02) : "#FAFBFC",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: formData[item.name] ? theme.palette.primary.main : "#94A3B8",
                          },
                        }}
                      >
                        <CustomCheckbox
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="#1A1A2E">
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.65rem" }}>
                                {item.desc}
                              </Typography>
                            </Box>
                          }
                          name={item.name}
                          checked={formData[item.name] || false}
                          onClick={(c) => handleCheckBox(item.name, c)}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            pt: 2,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/clients")}
            disabled={isSubmitting}
            startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {isSubmitting ? "Creating..." : "Create Visit"}
          </Button>
        </Box>
      </form>

      <FundingModal show={showFundingModal} onHide={() => setShowFundingModal(false)} clientID={clientID} />
      <CarePlanPickerDialog
        open={showCarePlanDialog}
        onClose={() => setShowCarePlanDialog(false)}
        clientID={clientID}
        onSelect={handleCarePlanSelect}
      />
    </Box>
  );
};

export default CreateClientVisit;